import rulesJson from "./component-rules.json";

export type ComponentType =
  | "Section"
  | "Container"
  | "Heading"
  | "Text"
  | "Image"
  | "Button"
  | "Header"
  | "Footer"
  | "Card"
  | "FeatureGrid"
  | "Badge"
  | "Divider"
  | "Spacer";

export type ParentContext = "Page" | "Document" | ComponentType;

export interface ComponentRule {
  label: string;
  isContainer: boolean;
  allowedParents: ParentContext[];
  defaultProps: Record<string, unknown>;
}

const rules = (rulesJson as { components: Record<string, ComponentRule> })
  .components;

export const COMPONENT_TYPES = Object.keys(rules) as ComponentType[];

export function isComponentType(value: string): value is ComponentType {
  return Object.prototype.hasOwnProperty.call(rules, value);
}

export function getComponentRule(type: string): ComponentRule | undefined {
  return rules[type];
}

export function defaultPropsFor(type: ComponentType): Record<string, unknown> {
  const rule = rules[type];
  if (!rule) throw new Error(`Unknown component type: ${type}`);
  return structuredClone(rule.defaultProps);
}

export interface NestingIssue {
  nodeId: string;
  type: string;
  reason: string;
}

interface NodeLike {
  id: string;
  type: string;
  children?: NodeLike[];
}

/**
 * Validates allowedParents for every node in a node tree (PRD §28).
 * `parent` is "Page" or "Document" at the roots.
 */
export function validateNesting(
  nodes: NodeLike[],
  parent: ParentContext,
): NestingIssue[] {
  const issues: NestingIssue[] = [];
  for (const node of nodes) {
    const rule = rules[node.type];
    if (!rule) continue; // unknown components are handled by the renderer
    if (!rule.allowedParents.includes(parent)) {
      issues.push({
        nodeId: node.id,
        type: node.type,
        reason: `${node.type} cannot be placed inside ${parent}`,
      });
    }
    if (rule.isContainer && node.children?.length) {
      issues.push(
        ...validateNesting(node.children, node.type as ComponentType),
      );
    } else if (!rule.isContainer && node.children?.length) {
      issues.push({
        nodeId: node.id,
        type: node.type,
        reason: `${node.type} cannot contain children`,
      });
    }
  }
  return issues;
}
